import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Estimate, EstimateVersion } from "../../models/estimate.model";
import { BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";
import { isEmpty } from "../../helpers/forms"
import { DepositValues } from "projects/common/src/lib/models/clientEstimate.model";
import { BusinessService } from "../../services/business.service";
import moment from 'moment';
import { UtilsService } from "projects/common/src/lib/services";

@Component({
  selector: 'app-estimate-preview-and-send',
  templateUrl: './estimate-preview-and-send.component.html',
  styleUrls: ['./estimate-preview-and-send.component.scss']
})
export class EstimatePreviewAndSendComponent implements OnInit, OnChanges {

  protected readonly isEmpty = isEmpty;

  @Input() estimate!: Estimate;
  @Input() currentEstimateVersion!: EstimateVersion;
  @Input() sentMode? = false;

  paidAmount = 0;

  dateSubject = new BehaviorSubject<Date>(new Date());
  date$ = this.dateSubject.asObservable().pipe(
    map(date => {
      return {
        createdDate: moment(date).format('MM/DD/YYYY'),
        createdTime: moment(date).format('h:mmA')
      }
    })
  );

  badge?: 'preview' | 'not-opened' | 'pending' | 'pending-incomplete' | 'declined' | 'won' | 'canceled';

  subtotal: number = 0;
  discount: number = 0;
  salesTax: number = 0;
  total: number = 0;

  business$ = this.businessService.selectedBusiness$;

  previewScaleFactor$ = this.utilsService.viewportWidth$.pipe(
      map(viewportWidth => {
        if((viewportWidth - 50) > 525) {
          return 1;
        }
        return (viewportWidth - 50) / 525;
      })
  );

  constructor(
    private businessService: BusinessService,
    private utilsService: UtilsService
  ) { }

  async ngOnInit() {
    if (!this.sentMode) {
      this.badge = 'preview';
      setInterval(() => {
        this.dateSubject.next(new Date());
      }, 1000);
    } else {
      this.dateSubject.next(this.currentEstimateVersion.clientSentAt!);
    }

    this.calculatePreviewSummary();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if ('currentEstimateVersion' in changes) {
      this.calculatePreviewSummary();
      this.paidAmount = this.estimate.sPaidAmount!;
      this.setBadge();
      // if ('clientEstimate' in changes)
      //   if (this.clientEstimate) {
      //     this.now.next(this.clientEstimate.createdAt!);
      //
      //   if (this.sentMode) {
      //     this.now.next(this.currentEstimateVersion.clientSentAt!);
      //     this.uidData = this.currentEstimateVersion.clientUid!;
      //   }
      // }
      
      if (this.sentMode)
        this.dateSubject.next(this.currentEstimateVersion.clientSentAt!);

      if (this.estimate.canceledFromPayment)
        this.badge = 'canceled';
    }
  }

  setBadge() {
    if (!this.currentEstimateVersion.opened && this.currentEstimateVersion.clientStatus !== 'canceled' && this.currentEstimateVersion.clientStatus !== 'declined') {
      this.badge = 'not-opened';
      return;
    }
    switch (this.currentEstimateVersion.clientStatus) {
      case 'pending':
        this.badge = 'pending';
        return;
      case 'pending_incomplete':
        this.badge = 'pending-incomplete';
        return;
      case 'paid':
        this.badge = 'won';
        return;
      case 'canceled':
        this.badge = 'canceled';
        return;
      case 'accepted':
        this.badge = 'won';
        return;
      case 'declined':
        this.badge = 'declined';
        return;
      case 'in_progress':
        this.badge = 'won';
        return;
    }
  }

  nextSubUid() {
    return Math.max(...this.estimate.versions.map(v => v.subUid ?? 0)) + 1;
  }

  getItemTaxAmount(price: number, taxable: boolean, discountAmount: number | null): number {
    if (!taxable)
      return 0;

    let discountedPrice = price;
    if (discountAmount)
      discountedPrice -= discountAmount;
    return discountedPrice*this.currentEstimateVersion.clientSalesTaxPercentage!/100;
  }

  getItemTotal(price: number, taxable: boolean, discountAmount: number | null, quantity: number | null): number {
    const discountedPrice = discountAmount
        ? (price - discountAmount)
        : price;
    const taxed = taxable
        ? discountedPrice + (discountedPrice*this.currentEstimateVersion.clientSalesTaxPercentage!/100)
        : discountedPrice;

    return quantity ? quantity*taxed : taxed;
  }

  calculatePreviewSummary() {
    this.subtotal = 0;
    this.discount = 0;
    this.salesTax = 0;
    this.total = 0;

    this.currentEstimateVersion.items?.forEach(item => {
      let discounted = 0;
      let taxed = 0;

      this.subtotal += (item.quantity ?? 0) * item.price;
      this.discount += (item.discountAmount ?? 0) * (item.quantity ?? 0);

      discounted = item.price - (item.discountAmount ?? 0);

      taxed = !item.taxable
        ? discounted
        : discounted + (discounted * (this.currentEstimateVersion.clientSalesTaxPercentage ?? 0) / 100);

      if (item.quantity && item.taxable && item.price)
        this.salesTax += parseFloat(((taxed - discounted) * item.quantity).toFixed(2));

      if (item.quantity)
        this.total += parseFloat((taxed * item.quantity).toFixed(2));
    });

    if (this.estimate?.sSurchargeSum)
      this.total += this.estimate.sSurchargeSum;
  }

  getDepositValues(): DepositValues | null {
    if (!this.estimate.sBalanceDue || !this.currentEstimateVersion.depositAmount)
      return null;

    return {
      full: this.currentEstimateVersion.depositAmount + (this.estimate.sSurchargeSum ?? 0),
      paid: this.estimate.sPaidAmount ?? 0,
      pending: this.estimate.sPaidAmount ? (this.currentEstimateVersion.depositAmount + (this.estimate.sSurchargeSum ?? 0)) - this.estimate.sPaidAmount : 0
    };
  }

  getBadgeCopy(badgeId: string): string | null {
    switch (badgeId) {
      case 'preview':
        return 'Preview';
      case 'not-opened':
        return 'Not Opened';
      case 'pending':
        return 'Pending';
      case 'pending-incomplete':
        return 'Pending';
      case 'won':
        return 'Won';
      case 'canceled':
        return 'Canceled';
      case 'declined':
        return 'Declined';
      default:
        return null;
    }
  }
  
  get isMobile() {
    return this.utilsService.isMobile();
  }
}
